import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FormControlLabel, Stack, Typography } from '@mui/material'
import { Formik, FormikProps } from 'formik'
import React, { FC, useRef } from 'react'
import { stringSchema } from 'src/Configuration/validation'
import { TextField } from 'src/Libs/inputs/formik'
import Checkbox from 'src/Libs/inputs/formik/Checkbox'
import { SearchFieldBaseOption } from 'src/Libs/inputs/formik/SearchFieldBase'
import { InstallerWithAccessData } from 'src/Libs/retrofit'
import { UsersSearch } from 'src/Libs/user/components'
import { boolean, number, object } from 'yup'
import { useGrantInstallerAccess } from '../../hooks/use-grant-installer-access'

const MIN_REASON_LENGTH = 3
const MAX_REASON_LENGTH = 1000
const FLOW_INSTALLER_FILTER = 'user.level=INSTALLER,user.source=hager'

export type GrantInstallerAccessModalProps = {
  onClose?: () => void
  installationId: string
  installers: InstallerWithAccessData[]
  refetchData: () => void
}

type GrantInstallerAccessFormData = {
  userId: number | undefined
  readPermission: boolean
  writePermission: boolean
  reason: string
}

/**
 * Modal to grant a Flow installer (individual Hager user) access to an installation.
 *
 * Mirrors the E3/DC "grant company access" flow, but selects an individual installer user instead
 * of a company. The already-attached installers are passed in so the same user cannot be added twice.
 * @param onClose - function to close the modal
 * @param installationId - installation id
 * @param installers - installers that already have access, used to prevent duplicates
 * @param refetchData - function to refetch the installation data with attached installers
 */
const GrantInstallerAccessModal: FC<GrantInstallerAccessModalProps> = ({ onClose, installationId, installers, refetchData }) => {
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const formRef = useRef<FormikProps<GrantInstallerAccessFormData>>(null)

  const { mutate: grantAccess, isPending } = useGrantInstallerAccess({
    onSuccess: () => {
      enqueueSuccessStack(t('installation.details.info.grant-access.success-message'))
      refetchData()
      onClose?.()
    },
    onError: () => enqueueErrorStack(t('installation.details.info.grant-access.error-message')),
  })

  const attachedAccountIds = installers.flatMap(installer => (installer.accountId === null ? [] : [installer.accountId]))
  const initialValues: GrantInstallerAccessFormData = {
    userId: undefined,
    readPermission: true,
    writePermission: false,
    reason: '',
  }

  const validationSchema = object().shape({
    userId: number()
      .integer()
      .positive()
      .notOneOf(attachedAccountIds, t('installation.details.info.grant-installer-access.installer-is-used-error'))
      .required(),
    readPermission: boolean().required(),
    writePermission: boolean().required(),
    reason: stringSchema(t)({ minLength: MIN_REASON_LENGTH, maxLength: MAX_REASON_LENGTH }).required(),
  })

  const onSubmit = ({ userId, writePermission, reason }: GrantInstallerAccessFormData): void => {
    if (userId === undefined) {
      enqueueErrorStack(t('installation.details.info.grant-access.error-message'))
      return
    }

    grantAccess({
      installationId: Number.parseInt(installationId),
      userId,
      writePermission,
      reason,
    })
  }

  const dialogActions: DialogActions = {
    primary: {
      name: t('installation.details.info.grant-access.submit-button-title'),
      onClick: () => formRef.current?.handleSubmit(),
      disabled: isPending,
    },
    secondary: [
      {
        name: t('installation.details.info.grant-access.cancel-button-title'),
        onClick: () => onClose?.(),
        disabled: isPending,
      },
    ],
  }

  const onChangeInstaller = (value: SearchFieldBaseOption | null): void => {
    formRef.current?.setFieldValue('userId', value?.value)
  }

  return (
    <Dialog title={t('installation.details.info.grant-access.dialog-title')} actions={dialogActions} maxWidth="sm" disableClose>
      <Typography variant="subtitle2" mb={4}>
        {t('installation.details.info.grant-installer-access.dialog-text')}
      </Typography>
      <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema} innerRef={formRef}>
        <form noValidate>
          <Stack spacing={4}>
            <UsersSearch
              name="userId"
              label={t('installation.details.info.grant-installer-access.installer-field-title')}
              additionalFilter={FLOW_INSTALLER_FILTER}
              maxDisplayedResultCount={10}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onValueChange={onChangeInstaller}
              disabled={isPending}
            />
            <Stack direction="row" justifyContent="center" spacing={4}>
              <FormControlLabel
                control={<Checkbox name="readPermission" />}
                label={t('installation.access-and-invitations.table.header.columns.can-read')}
                disabled
              />
              <FormControlLabel
                control={<Checkbox name="writePermission" />}
                label={t('installation.access-and-invitations.table.header.columns.can-write')}
                disabled={isPending}
              />
            </Stack>
            <Typography variant="subtitle2">{t('installation.details.info.grant-access.reason-field-text')}</Typography>
            <TextField label={t('installation.details.info.grant-access.reason-field-title')} name="reason" multiline required disabled={isPending} />
          </Stack>
        </form>
      </Formik>
    </Dialog>
  )
}

export default GrantInstallerAccessModal
